/**
 * Everyone at an event who is not a competitor — D62.
 *
 * The MC and sometimes a co-host, the arena director, head dancers, the host drum and co-host
 * drum, invited traditional drums, flag and staff carriers, elders, the stickman, organisers,
 * volunteers, staff. Some are appointed months ahead; some, as the domain experts put it, "might
 * just show up". A pow wow pays far more people than it places.
 *
 * None of them could be paid before this. Every payable thing in the system was a
 * `registration_entry` — participant × competition × session — so a person with no competition had
 * no entry, could not be checked in, and could not appear on a payout sheet.
 *
 * The shortcut is a fixed list of paid roles in an enum, and it is rejected for the reason D14–D21
 * rejects every other fixed list: the next community has a firekeeper, a whip man, a head veteran
 * or a water carrier, and none of them should need a code change. A role is a row.
 */

import { index, sqliteTable, text, uniqueIndex, integer } from "drizzle-orm/sqlite-core";
import { archivable, audited, cents, currency, flag, id, ref, sortOrder } from "./columns.js";

/** A host drum is a group; an arena director is a person; an elder could be listed either way. */
export const ROLE_SUBJECT_KINDS = ["person", "group", "either"] as const;

/**
 * A role, hanging off an event — or off nothing at all, which is a **template**.
 *
 * The nullable `eventId` plus `isTemplate` is the shape `category_dimensions` and `scoring_configs`
 * already use for the same reason (D18, D43): a community writes "MC, Arena Director, Head Dancers,
 * Host Drum, Elders, Firekeeper" once and starts every subsequent event from it, without a second
 * table that means almost the same thing. Every read is by `eventId`, which a template's null can
 * never match, so a template cannot leak into a live event by omission.
 *
 * Note what is **not** here: a `paid` flag. Whether a role is paid is derived from whether a
 * payment rule targets it (D63), on the same reasoning as D36 — a stored flag and the rules can
 * disagree, and here the disagreement is money.
 */
export const eventRoles = sqliteTable(
  "event_roles",
  {
    id: id(),
    communityId: ref("community_id").notNull(),
    /** Null on a template, which belongs to the community rather than to an event. */
    eventId: ref("event_id"),
    /** The community's own word for it. Never translated, never mapped to an internal enum. */
    name: text("name").notNull(),
    description: text("description"),
    /** How the roster groups it on screen — "Arena", "Drums", "Volunteers". Free text. */
    groupLabel: text("group_label"),

    subjectKind: text("subject_kind", { enum: ROLE_SUBJECT_KINDS }).notNull().default("person"),

    /**
     * How many people hold it. `minCount` is what readiness reads to say a role is unfilled — the
     * arena director nobody remembered to name is a problem discovered on the day otherwise.
     * `maxCount` null is unbounded, which is head dancers, elders and flag carriers.
     *
     * Deliberately the same idiom as `competitions.rosterMin`/`rosterMax` rather than a second
     * spelling of the same idea: "arena director — only one" and "head dancers — multiple" are one
     * mechanism.
     */
    minCount: integer("min_count").notNull().default(0),
    maxCount: integer("max_count"),

    /**
     * Whether the gate expects this role to check in, which is a **roster** question and not a money
     * one.
     *
     * Deliberately not called `requiresCheckin`. A prize rule already carries that name, and it is
     * what decides whether attendance is required before money moves — for a role holder exactly as
     * for a dancer (D64). Two flags of the same name where only one governs payment is how a
     * community ends up believing it turned something off that it did not.
     *
     * What this one does is keep the check-in screen honest: a community that does not scan its
     * volunteers turns it off for that role, and they stop counting as not-yet-arrived.
     */
    expectsCheckin: flag("expects_checkin").notNull().default(true),

    /** The MC belongs on the poster; the gate volunteers do not. */
    publiclyListed: flag("publicly_listed").notNull().default(true),

    isTemplate: flag("is_template").notNull().default(false),
    sortOrder: sortOrder(),
    ...audited(),
    ...archivable(),
  },
  (table) => [
    index("event_roles_event_idx").on(table.eventId, table.sortOrder),
    index("event_roles_template_idx").on(table.communityId, table.isTemplate),
  ],
);

export const ROLE_ASSIGNMENT_STATUSES = ["expected", "confirmed", "withdrawn"] as const;

/**
 * Who holds a role.
 *
 * This is the upgraded `event_team` rather than a new table beside it. Two lists of who the MC is
 * drift the day someone edits one of them, and the old one already fed the public page.
 *
 * `displayName` stays free text and `userId` stays optional, because most of these people never log
 * in. `registrationId` exists because drums and flag carriers *do* register, so the same person is
 * one record on both paths rather than two records that disagree.
 *
 * **Listing someone costs nothing; paying them needs a payee.** An assignment carrying only a
 * `displayName` is a poster credit, and that is a complete and valid thing to be. A `participantId`
 * or a `teamId` is what makes it payable. That line is drawn in validation rather than here on
 * purpose: requiring a payee up front would put paperwork between an organiser and a poster, and
 * the person who might just show up is exactly the one it would fail.
 */
export const eventRoleAssignments = sqliteTable(
  "event_role_assignments",
  {
    id: id(),
    eventId: ref("event_id").notNull(),
    communityId: ref("community_id").notNull(),
    eventRoleId: ref("event_role_id").notNull(),

    /** The payee, when there is one. A person, or a group — a host drum is paid as a drum. */
    participantId: ref("participant_id"),
    teamId: ref("team_id"),
    /** Set when this person also has an account. Most never do. */
    userId: ref("user_id"),

    displayName: text("display_name").notNull(),
    groupName: text("group_name"),

    /** Present when they came through registration, which drums and flag carriers do. */
    registrationId: ref("registration_id"),

    /**
     * What was agreed with this person, for a rule whose basis is `agreed` (D63). The MC's fee is
     * negotiated with the MC — one rule, many amounts — where an elders' honorarium is a community
     * rate that applies to whoever appears.
     */
    agreedAmountCents: cents("agreed_amount_cents"),
    currency: currency(),

    status: text("status", { enum: ROLE_ASSIGNMENT_STATUSES }).notNull().default("confirmed"),
    notes: text("notes"),
    sortOrder: sortOrder(),
    ...audited(),
    ...archivable(),
  },
  (table) => [
    index("event_role_assignments_event_idx").on(table.eventId, table.sortOrder),
    index("event_role_assignments_role_idx").on(table.eventRoleId),
    index("event_role_assignments_participant_idx").on(table.participantId),
  ],
);

/**
 * Which sessions an assignment covers.
 *
 * An empty set means every session, exactly as it does for a registration window (D49) and a
 * competition (D50). That convention is what pays an elder who came only on the Sunday for the
 * Sunday, and an MC who worked one session of four for one session. No `archivable()`: like every
 * other link table here, it lives and dies with the row.
 */
export const eventRoleAssignmentSessions = sqliteTable(
  "event_role_assignment_sessions",
  {
    id: id(),
    assignmentId: ref("assignment_id").notNull(),
    sessionId: ref("session_id").notNull(),
    communityId: ref("community_id").notNull(),
    ...audited(),
  },
  (table) => [
    uniqueIndex("event_role_assignment_sessions_unique").on(table.assignmentId, table.sessionId),
    index("event_role_assignment_sessions_session_idx").on(table.sessionId),
  ],
);
